import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import usePayment from '../../hooks/usePayment';

interface CampaignContextPayload {
  packageId?: string | null;
  packageName?: string | null;
  adId?: string | null;
}

export interface PaymentFormProps {
  onAmountChange?: (amount: number | null) => void;
  initialAmount?: number | null;
  campaignContext?: CampaignContextPayload | null;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  onAmountChange,
  initialAmount,
  campaignContext,
}) => {
  const [amount, setAmount] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { processPayment, error: remoteError, paymentResult } = usePayment();
  const navigate = useNavigate();
  const location = useLocation();
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    if (typeof initialAmount === 'number' && initialAmount > 0) {
      const value = String(initialAmount);
      setAmount(value);
      if (onAmountChange) {
        onAmountChange(initialAmount);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!amount) {
      setLocalError('Amount is required.');
      return;
    }

    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setLocalError('Please enter a valid amount.');
      return;
    }

    if (!stripe || !elements) {
      setLocalError('Payments are not ready yet. Please try again.');
      return;
    }

    setSubmitting(true);
    try {
      const card = elements.getElement(CardElement);
      if (!card) {
        setLocalError('Card details are not ready.');
        return;
      }

      const pmResult = await stripe.createPaymentMethod({
        type: 'card',
        card,
      });

      if (pmResult.error || !pmResult.paymentMethod) {
        const msg = pmResult.error?.message || 'Unable to create payment method.';
        setLocalError(msg);
        return;
      }

      const payload: Record<string, unknown> = {
        amount: numericAmount,
        stripePaymentMethodId: pmResult.paymentMethod.id,
        campaignPackageId: campaignContext?.packageId ?? undefined,
        campaignPackageName: campaignContext?.packageName ?? undefined,
        campaignAdId: campaignContext?.adId ?? undefined,
      };
      const payment = await processPayment(payload);

      if (!payment) {
        setLocalError('Payment could not be processed. Please try again.');
        return;
      }

      if (!payment.clientSecret) {
        setLocalError('Stripe payment setup did not return a client secret.');
        return;
      }

      const confirmResult = await stripe.confirmCardPayment(payment.clientSecret, {
        payment_method: pmResult.paymentMethod.id,
      });

      if (confirmResult.error) {
        setLocalError(confirmResult.error.message || 'Unable to confirm payment.');
        return;
      }

      if (typeof window !== 'undefined') {
        try {
          window.dispatchEvent(new CustomEvent('tellurvision-payment-success'));
        } catch {
          // ignore event dispatch failures
        }
      }

      setAmount('');
      if (onAmountChange) {
        onAmountChange(null);
      }

      const packageName = campaignContext?.packageName ?? null;
      const currency = 'USD';

      const referenceId = payment._id ?? payment.id ?? null;
      const createdAt = payment.createdAt ?? payment.date ?? new Date().toISOString();

      navigate('/payment/confirmation', {
        state: {
          amount: numericAmount,
          currency,
          packageName,
          packageId: campaignContext?.packageId ?? null,
          adId: campaignContext?.adId ?? null,
          referenceId,
          createdAt,
          from: location.pathname,
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  const effectiveError = localError || remoteError || null;
  const showSuccess = !effectiveError && paymentResult && paymentResult.success;

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Payment details</h2>
        <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
          Enter your card details to fund your next TELLURVISION campaign through Stripe.
        </p>
      </div>
      {effectiveError && <p className="text-xs text-red-600">{effectiveError}</p>}
      {showSuccess && !effectiveError && (
        <p className="text-xs text-emerald-600">Payment processed successfully.</p>
      )}
      <form onSubmit={handleSubmit} className="mt-2 space-y-3 text-sm">
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">Amount (USD)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => {
              const next = e.target.value;
              setAmount(next);
              if (onAmountChange) {
                const parsed = Number(next);
                onAmountChange(Number.isFinite(parsed) && parsed > 0 ? parsed : null);
              }
            }}
            className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">Card number</label>
          <div className="mt-1 rounded-md border border-slate-300 dark:border-slate-600 px-2 py-2 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '14px',
                    color: '#0f172a',
                    '::placeholder': { color: '#9ca3af' },
                  },
                  invalid: { color: '#b91c1c' },
                },
              }}
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="mt-2 inline-flex items-center justify-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:bg-slate-400"
        >
          {submitting ? 'Processing…' : 'Pay now'}
        </button>
      </form>
    </div>
  );
};

export default PaymentForm;
